import React, { useRef, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { Link } from "react-router-dom";
import { IFeedPostItem, postShareDescription, postShareTitle } from "@eagerdog/interfaces";
import moment from "moment";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import ShareModal from "src/components/ShareModal/ShareModal";
import RunSummary, { sportLabel } from "./RunSummary";
import ActivityBlock, { activityLink } from "./ActivityBlock";
import PostCardMenu, { EditedMarker, ReportedPlaceholder, UnderReviewChip, useRequireLogin } from "./PostCardMenu";
import { postPath, postShareUrl } from "./postLinks";

import styles from "./FeedCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    onChange: () => void;
    /** True on /p/:id — the timestamp is plain text instead of a link to itself. */
    isPermalink?: boolean;
}

const POST_TYPE_LABEL: Record<string, string> = {
    photo: "shared a photo",
    video: "shared a video",
    run_result: "logged a run",
    club_followed: "started following",
    dog_followed: "started following",
};

/** A name that links to its page when we know the id; plain text otherwise. */
const nameLink = (name: string | undefined, to: string | undefined): React.ReactNode =>
    name && to ? <Link to={to} className={styles.nameLink}>{name}</Link> : name;

// Header sentence: "<Author> <verb> [<dog>] [<object>]".
const headline = (post: IFeedPostItem, who: string): React.ReactNode => {
    const dog = nameLink(post.dog_call_name, post.dog_id ? `/dogs/${post.dog_id}` : undefined);
    const targetId = post.activity?.target_id;
    const target = nameLink(
        post.activity?.target_name,
        targetId ? (post.post_type === Constants.post_type.club_followed ? `/k/${targetId}` : `/dogs/${targetId}`) : undefined
    );
    switch (post.post_type) {
        case Constants.post_type.event_registered: {
            // "You entered the event <Event>"; the dogs are shown on the card below.
            const event = nameLink(post.activity?.event_title, activityLink(post));
            return event
                ? <>{who}<span className={styles.plain}> entered the event </span>{event}</>
                : <>{who}<span className={styles.plain}> entered an event</span></>;
        }
        case Constants.post_type.course_enrolled:
            return dog
                ? <>{who}<span className={styles.plain}> enrolled </span>{dog}<span className={styles.plain}> in a course</span></>
                : <>{who}<span className={styles.plain}> enrolled in a course</span></>;
        case Constants.post_type.club_followed:
        case Constants.post_type.dog_followed:
            return <>{who}<span className={styles.plain}> started following </span>{target}</>;
        case Constants.post_type.result_entered:
            // Mirrors the push notification: "Jimmy's result is in".
            return dog
                ? <>{dog}<span className={styles.plain}>'s result is in</span></>
                : <>{who}<span className={styles.plain}> got a result</span></>;
        default: {
            const action = post.post_type === Constants.post_type.run_result && post.run_summary
                ? `logged ${/^[aeiou]/i.test(sportLabel(post.run_summary)) ? "an" : "a"} ${sportLabel(post.run_summary)} run`
                : (POST_TYPE_LABEL[post.post_type] || "posted");
            return <>{who}<span className={styles.plain}>{dog ? ` ${action} for ` : ` ${action}`}</span>{dog}</>;
        }
    }
};

const OwnerPostCard: React.FC<IProps> = ({ post, onChange, isPermalink = false }) => {
    const user = helperService.getUser();
    const [liked, setLiked] = useState(!!post.liked_by_viewer);
    const [likeCount, setLikeCount] = useState(post.like_count || 0);
    const liking = useRef(false);
    const [reported, setReported] = useState(false);
    const shareModal = useModal();
    const requireLogin = useRequireLogin();
    const isAuthor = post.author_type === Constants.post_author_type.user && String(post.author_id) === String(user._id);
    const underReview = post.moderation_status === "under_review";

    const toggleLike = () => {
        if (!requireLogin()) return;
        if (liking.current) return;
        liking.current = true;
        const nextLiked = !liked;
        setLiked(nextLiked);
        setLikeCount((c) => c + (nextLiked ? 1 : -1));
        apiService.togglePostLike(post._id).then((result) => {
            if (typeof result?.like_count === "number") {
                setLiked(!!result.liked);
                setLikeCount(result.like_count);
            }
        }).catch(() => {
            setLiked(!nextLiked);
            setLikeCount((c) => c + (nextLiked ? -1 : 1));
        }).finally(() => {
            liking.current = false;
        });
    };

    // Dog-tagged posts lead with the dog; untagged activity (follows,
    // seminar enrollments) falls back to the author. Event entries lead with
    // the author too — the entered dogs are listed on the event card.
    const leadsWithAuthor = post.post_type === Constants.post_type.event_registered || !post.dog_call_name;
    const avatarUrl = leadsWithAuthor ? post.author_image_url : post.dog_image_url;
    const avatarName = (leadsWithAuthor ? post.author_display_name : post.dog_call_name) || "?";
    const avatarInitial = avatarName.charAt(0).toUpperCase();
    const avatarLink = !leadsWithAuthor && post.dog_id ? `/dogs/${post.dog_id}` : undefined;
    const who = isAuthor ? "You" : (post.author_display_name || "Someone");
    // Canonical permalink for every channel, so the link unfurls as this post.
    const shareUrl = postShareUrl(post);
    const shareTitle = postShareTitle(post);
    const timestamp = moment(post.created_at).fromNow();

    if (reported) return <ReportedPlaceholder />;

    return (
        <div className={styles.FeedCard}>
            <div className={styles.header}>
                {avatarLink ? (
                    <Link to={avatarLink} className={styles.avatar}>
                        {avatarUrl
                            ? <img src={avatarUrl} alt={avatarName} />
                            : <span>{avatarInitial}</span>}
                    </Link>
                ) : (
                    <div className={styles.avatar}>
                        {avatarUrl
                            ? <img src={avatarUrl} alt={avatarName} />
                            : <span>{avatarInitial}</span>}
                    </div>
                )}
                <div className={styles.headerText}>
                    <span className={styles.authorName}>{headline(post, who)}</span>
                    <span className={styles.metaRow}>
                        {isPermalink
                            ? timestamp
                            : <Link to={postPath(post)} className={styles.timeLink}>{timestamp}</Link>}
                        <EditedMarker post={post} />
                    </span>
                </div>
                {underReview && <UnderReviewChip />}
                <PostCardMenu post={post} onChange={onChange} onReported={() => setReported(true)} shareUrl={shareUrl} />
            </div>
            {/* Caption first, then the run / activity details, same order as club posts. */}
            {post.body && <div className={styles.body}>{post.body}</div>}
            {post.run_summary && <RunSummary run={post.run_summary} />}
            {post.activity && <ActivityBlock post={post} />}
            {post.media && post.media[0] && (
                <div className={styles.media}>
                    {post.media[0].type === Constants.post_media_type.video
                        ? <video src={post.media[0].url} controls />
                        : <img src={post.media[0].url} alt="" />}
                </div>
            )}
            {(likeCount > 0 || post.share_count > 0) && (
                <div className={styles.countsRow}>
                    {likeCount > 0 && <span>{likeCount} Like{likeCount === 1 ? "" : "s"}</span>}
                    {post.share_count > 0 && <span>{post.share_count} Share{post.share_count === 1 ? "" : "s"}</span>}
                </div>
            )}
            <div className={styles.actionsRow}>
                <button className={liked ? styles.active : ""} onClick={toggleLike} disabled={underReview}>Like</button>
                <button onClick={() => { if (requireLogin()) shareModal.toggle(); }} disabled={underReview}>Share</button>
            </div>
            <Modal
                className="ShareModalContainer"
                isShown={shareModal.isShown}
                hide={shareModal.toggle}
                modalContent={
                    <ShareModal
                        eventTitle={shareTitle}
                        shareText={postShareDescription(post) || undefined}
                        eventUrl={shareUrl}
                        shareType="post"
                        onClose={shareModal.toggle}
                        onShare={(channel) => {
                            apiService.recordPostShare(post._id, channel).then(onChange).catch(() => {});
                        }}
                    />
                }
            />
        </div>
    );
};

export default OwnerPostCard;
